import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Plus } from "lucide-react";
import { isBookCollectionKey, knowledgeBaseLabel } from "@/lib/knowledgeBaseRegistry";
import {
  articlePath,
  formatKnowledgeDate,
  knowledgeStatusLabel,
  type KnowledgeArticle,
} from "@/lib/knowledgeModel";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgePrimaryLinkClass,
  knowledgeTableWrapClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { UploadMarkdownButton } from "@/pages/knowledge/UploadMarkdownButton";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { knowledgeArticleNewPath } from "@/lib/knowledgePaths";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

function bookLabels(article: KnowledgeArticle, titles: Map<string, string>): string {
  const keys = article.collectionKeys.filter(isBookCollectionKey);
  if (keys.length === 0) return "Standalone";
  return keys.map((key) => titles.get(key) || key).join(", ");
}

export function KnowledgeArticlesPage() {
  const { articles, loading, error } = useKnowledgeArticles();
  const { books: customBooks } = useCustomBooks();
  const { active, hasBases, activeId } = useKnowledgeBases();
  const titles = new Map(customBooks.map((book) => [book.collectionKey, book.title] as const));
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title="Articles"
        description="Markdown articles live inside a knowledge base. Create a knowledge base first."
        badge="Markdown"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title="Articles"
      description={`Markdown articles in "${active ? knowledgeBaseLabel(active) : "this knowledge base"}". Books are optional. Create articles here, upload a .md file, then assign books later if you want.`}
      badge="Markdown"
      actions={
        <>
          <UploadMarkdownButton onError={setUploadError} />
          <Link to={knowledgeArticleNewPath(activeId)} className={knowledgePrimaryLinkClass}>
            <Plus className="h-4 w-4" aria-hidden />
            New
          </Link>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {uploadError ? <KnowledgeError message={uploadError} /> : null}
      <div className={knowledgeTableWrapClass}>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900 dark:border-white/10 dark:text-white">
          <FileText className="h-4 w-4" aria-hidden />
          All articles
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 font-semibold">Title</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Books</th>
              <th className="px-4 py-3 text-right font-semibold">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  Loading articles…
                </td>
              </tr>
            ) : articles.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center">
                  <p className="text-base font-semibold text-slate-900 dark:text-white">0 articles</p>
                  <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                    Nothing is created for you. Use New article or Upload markdown (.md). A book is
                    not required.
                  </p>
                  <Link
                    to={knowledgeArticleNewPath(activeId)}
                    className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    New article
                  </Link>
                </td>
              </tr>
            ) : (
              articles.map((article) => (
                <tr key={article.id} className="border-b border-slate-100 dark:border-white/5">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                    <Link
                      to={articlePath(activeId, article.id)}
                      className="text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {article.title || "Untitled"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {knowledgeStatusLabel(article.status)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {bookLabels(article, titles)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">
                    {formatKnowledgeDate(article.updatedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </KnowledgeChrome>
  );
}
